import { useState, useEffect, useContext } from "react";
import Navbar from "./blocks/Navbar.js";
import { Typography, Box, Paper, Tabs, Tab, Icon } from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import CalendarList from "./blocks/CalendarList.js";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import ActionCard from "./blocks/ActionCard.js";
import LoadingAnimation from "./blocks/LoadingAnimation.js";
import {
  copyCalendarLinkToClipboard,
  handleCustomerPortal,
  handleResubscribe as handleResubscribeUtil,
  handleUnsubscribe,
} from "src/utils/Utils.js";
import { AuthContext } from "src/utils/AuthContext.js";
import {
  getPageRoute,
  useGetUserCalendars,
  useUnsubscribeGroup,
  useUnsubscribeCalendar,
  useRemoveFromGroup,
} from "src/services";
import Footer from "./blocks/Footer.js";
import { useTheme } from "@emotion/react";
import { revealNavbar, sortAlphabetically } from "src/utils/Utils.js";
import { useSnackbarContext } from "src/utils/SnackbarContext.js";
import ShareDialog from "./blocks/dialogs/ShareDialog.js";
import OpenCalendarDialog from "./blocks/dialogs/OpenCalendarDialog.js";
import { useFeatureAccess } from "src/utils/FeatureAccessContext.js";
import {
  Features,
  hasAccess,
  isGoogleCalendarConnectionCandidate,
  getUpgradeFlow,
} from "src/services/FeatureGatingService.js";
import { useGroupDialogs } from "src/utils/useGroupDialogs.js";
import NewGroupDialog from "./blocks/dialogs/NewGroupDialog.js";
import AddToGroupDialog from "./blocks/dialogs/AddToGroupDialog.js";
import { Helmet } from "react-helmet";
import DeleteCalendarDialog from "./blocks/dialogs/DeleteCalendarDialog.js";
import DeleteGroupDialog from "./blocks/dialogs/DeleteGroupDialog.js";
import { useDeleteCalendarGroup } from "src/services/mutations/useDeleteCalendarGroup.js";
import { useGoogleCalendarStatus } from "src/services/queries/useGoogleCalendarStatus.js";
import { useForceRefreshGoogleCalendar } from "src/services/mutations/useForceRefreshGoogleCalendar.js";
import LibraryMessage from "./blocks/library/LibraryMessage.js";
import UnlimitedCalendarsUpgradeBanner from "./explainers/banners/UnlimitedCalendarsUpgradeBanner.js";
import GroupsUpgradeBanner from "./explainers/banners/GroupsUpgradeBanner.js";
import GoogleConnectUpgradeBanner from "./explainers/banners/GoogleConnectUpgradeBanner.js";

/**
 * Library Component
 *
 * Displays user's added calendars, created groups, and imported calendars.
 *
 * ============================================================================
 * PAYMENT UPGRADE FLOW PATTERN (Context Approach)
 * ============================================================================
 *
 * This component uses the FeatureAccessContext for centralized feature gating.
 * The pattern handles three cases when a user tries to access a gated feature:
 *
 * 1. User already has access → Proceed immediately
 * 2. User is simple payment eligible → Process payment → Show dialog → User clicks Continue → Proceed
 * 3. User is not eligible → Redirect to upgrade page
 *
 * Implementation:
 * ───────────────
 *
 * 1. Get hook from context:
 *    const { requestFeatureAccess } = useFeatureAccess();
 *
 * 2. Request access with action callback:
 *    const result = await requestFeatureAccess(Features.SOME_FEATURE.key, {
 *      calendar,
 *      action: () => performAction(calendar),     // ← Executed after dialog
 *      continueText: "Continue with Action",
 *    });
 *
 * 3. Handle immediate execution (user already has access):
 *    if (result.granted && result.reason === "has_access") {
 *      performAction(calendar);  // Execute immediately
 *    }
 *
 * Benefits of Context Approach:
 * ─────────────────────────────
 * - ✅ No dialog state management needed
 * - ✅ No dialog component rendering needed
 * - ✅ No loading state management needed (handled globally)
 * - ✅ Action logic defined inline with request
 * - ✅ Automatic queue management for multiple requests
 * - ✅ Single dialog instance app-wide
 *
 * See also:
 * - /utils/FeatureAccessContext.js - Context implementation
 * - /services/FeatureGatingService.js - Feature definitions
 * - /docs/PAYMENT_UPGRADE_FLOW_PATTERN.md - Full documentation
 */

function Library({ user }) {
  const [importedCalendars, setImportedCalendars] = useState([]);
  const [createdGroups, setCreatedGroups] = useState([]);
  const [addedItems, setAddedItems] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchParams] = useSearchParams();
  let { tab } = searchParams;
  tab =
    tab ||
    new URLSearchParams(window.location.search).get("tab") ||
    "calendars";
  const [activeTab, setActiveTab] = useState(tab);
  if (!tab || activeTab)
    window.history.replaceState({}, "", `?tab=${activeTab}`);
  const [openCalendar, setOpenCalendar] = useState(false);
  const [openGroup, setOpenGroup] = useState(false);
  const [deleteText, setDeleteText] = useState("");
  const [deleteMessage, setDeleteMessage] = useState("");
  const [targetDeleteCalendar, setTargetDeleteCalendar] = useState(null);
  const [openCalendarDialog, setOpenCalendarDialog] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState({
    action: null, // "resubscribe" or "add-to-group" or "switch-google-connect"
    calendar: null,
  });
  const { requestFeatureAccess } = useFeatureAccess();
  const { refetchAuth } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const [shareDialogCalendar, setShareDialogCalendar] = useState(null);
  const {
    setSnackbarOpen,
    setSnackbarMessage,
    setSnackbarSeverity,
    showSnackBar,
  } = useSnackbarContext();
  const { mutateAsync: unsubscribeCalendar } = useUnsubscribeCalendar();
  const { mutateAsync: removeFromGroup } = useRemoveFromGroup();
  const { mutateAsync: unsubscribeGroup } = useUnsubscribeGroup();
  const {
    data: googleCalendarStatus,
    refetch: refetchGoogleCalendarStatus,
    isLoading: isLoadingGoogleCalendarStatus,
  } = useGoogleCalendarStatus({ user });
  const {
    handleForceRefreshGoogleCalendar,
    isLoading: isLoadingForceRefreshGoogleCalendarRequest,
    forceRefreshCalendarId,
  } = useForceRefreshGoogleCalendar({ refetchGoogleCalendarStatus });
  const isLoadingForceRefreshGoogleCalendar =
    isLoadingForceRefreshGoogleCalendarRequest ||
    (isLoadingGoogleCalendarStatus &&
      hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key));
  const {
    data: userCalendars,
    refetch: refetchUserCalendars,
    isLoading,
  } = useGetUserCalendars(
    { user },
    {
      onSuccess: (data) => {
        setImportedCalendars(
          sortAlphabetically(data.createdCalendars, "name") ?? []
        );
        setCreatedGroups(sortAlphabetically(data.createdGroups, "name") ?? []);
        setAddedItems(
          sortAlphabetically(
            [...(data.addedCalendars ?? []), ...(data.addedGroups ?? [])],
            "name"
          )
        );
        setInitialLoad(false);
      },
    }
  );
  const {
    openAddToGroup,
    openNewGroup,
    setOpenAddToGroup,
    setOpenNewGroup,
    handleGroupDialog,
    handleAddToGroup,
    handleCreateGroup,
    handleMessage,
    targetCalendar,
  } = useGroupDialogs({
    setCreatedGroups,
    moveCalendarToGroup: true,
  });

  useEffect(() => {
    refetchUserCalendars();
  }, []);

  const { mutateAsync: handleDeleteMutation, isLoading: isLoadingDelete } =
    useDeleteCalendarGroup();

  const handleDelete = async (event) => {
    event.preventDefault();
    if (deleteText !== "DELETE") {
      setDeleteMessage("Please check for typos.");
      return;
    }
    const typeGroup = targetDeleteCalendar?.calendars;
    await handleDeleteMutation({
      typeGroup,
      calendar: targetDeleteCalendar,
    });
    refetchUserCalendars();
    handleCloseDeleteDialog();
  };

  // Open delete dialog box
  const handleClickOpenDeleteDialog = (calendar) => {
    setTargetDeleteCalendar(calendar);
    const typeGroup = calendar?.calendars;
    if (typeGroup) {
      setOpenGroup(true);
    } else {
      setOpenCalendar(true);
    }
  };

  // Close delete dialog box
  const handleCloseDeleteDialog = (event) => {
    event?.preventDefault();
    setDeleteText("");
    const typeGroup = targetDeleteCalendar?.calendars;
    if (typeGroup) {
      setOpenGroup(false);
    } else {
      setOpenCalendar(false);
    }
    setTargetDeleteCalendar(null);
  };

  const handleResubscribe = async (calendar) => {
    await handleResubscribeUtil({
      user,
      calendar,
      navigate,
      requestFeatureAccess,
      setSelectedCalendar,
      onGoogleProvider: () => setOpenCalendarDialog(true),
    });
  };

  const isDisabledForceRefresh = (calendar) => {
    const googleCalendarStatusId =
      googleCalendarStatus?.calendarsById?.[calendar._id]?.[0] ??
      googleCalendarStatus?.groupsById?.[calendar._id]?.[0] ??
      null;
    const isSyncing = googleCalendarStatusId?.status === "syncing";
    const isRefreshing =
      isLoadingForceRefreshGoogleCalendar &&
      forceRefreshCalendarId === calendar._id;
    return isRefreshing || isSyncing;
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "long",
        day: "2-digit",
      }).format(date);
    } catch (error) {
      console.error(error);
      showSnackBar(
        setSnackbarOpen,
        setSnackbarMessage,
        setSnackbarSeverity,
        "This browser isn't displaying dates correctly. Please use another browser.",
        "error"
      );
      return "Unknown date/time";
    }
  };

  const handleTabChange = async (event, key) => {
    revealNavbar();
    if (key === "groups") {
      refetchUserCalendars();
    }
    setActiveTab(key);
  };

  return (
    <div
      style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}
    >
      <Helmet>
        <title>Library - Stanza</title>
        <meta
          name="description"
          content="All the calendars you've added or imported."
        />
      </Helmet>
      <Navbar user={user} />
      <Box
        sx={{
          margin: "auto",
          maxWidth: { xs: "calc(100% - 2rem)", sm: "600px" },
          minWidth: { xs: "calc(100% - 2rem)", sm: "600px" },
          marginTop: 0,
          flexGrow: 1,
          p: 2,
        }}
      >
        <Typography
          as="h1"
          variant="title"
          sx={{ mb: 1, mt: { md: 3 }, fontWeight: "600" }}
        >
          Your Library
        </Typography>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 3 }}>
          Everything you've created or added.
        </Typography>
        <Tabs
          id="library-tabs"
          data-testid="library-tabs"
          value={activeTab}
          onChange={handleTabChange}
          variant={"fullWidth"}
        >
          <Tab label="Calendars" value="calendars" disableRipple />
          <Tab label="Groups" value="groups" disableRipple />
          <Tab label="Imports" value="imports" disableRipple />
        </Tabs>
        <Box role="tabpanel" hidden={activeTab !== "calendars"}>
          <LibraryMessage user={user} tab="calendar" sx={{ mb: 2 }} />
          {isLoading || initialLoad ? (
            <LoadingAnimation />
          ) : addedItems.length > 0 ? (
            <>
              <CalendarList
                id={"calendars"}
                user={user}
                calendars={addedItems}
                {...(!hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)
                  ? {
                      buttonIcon: (props) => (
                        <Icon
                          baseClassName="material-symbols-outlined"
                          {...props}
                        >
                          event_repeat
                        </Icon>
                      ),
                      buttonText: "Resubscribe",
                      buttonAction: handleResubscribe,
                    }
                  : {})}
                googleCalendarStatus={googleCalendarStatus}
                isLoadingForceRefreshGoogleCalendar={
                  isLoadingForceRefreshGoogleCalendar
                }
                forceRefreshCalendarId={forceRefreshCalendarId}
                threeDotsMenu={[
                  {
                    icon: (props) => (
                      <Icon
                        baseClassName="material-symbols-outlined"
                        {...props}
                      >
                        create_new_folder
                      </Icon>
                    ),
                    name: "Add to Group",
                    action: async (calendar) => {
                      const performAction = ({ user }) =>
                        handleGroupDialog(calendar, { user });

                      const result = await requestFeatureAccess(
                        Features.ADD_TO_GROUP.key,
                        {
                          calendar,
                          currentPath: window.location.pathname,
                          action: performAction,
                          continueText: "Continue Adding to Group",
                        }
                      );

                      if (result.granted && result.reason === "has_access") {
                        performAction({ user });
                      }
                    },
                  },
                  hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)
                    ? {
                        icon: (props) => (
                          <Icon
                            baseClassName="material-symbols-outlined"
                            {...props}
                          >
                            refresh
                          </Icon>
                        ),
                        name: "Force Refresh",
                        action: handleForceRefreshGoogleCalendar,
                        isDisabled: isDisabledForceRefresh,
                      }
                    : null,
                  {
                    icon: (props) => (
                      <Icon
                        baseClassName="material-symbols-outlined"
                        {...props}
                      >
                        link
                      </Icon>
                    ),
                    name: "Copy Link",
                    action: (calendar) => {
                      copyCalendarLinkToClipboard({
                        inGroup: false,
                        user,
                        calendar,
                        showSnackBar,
                        setSnackbarOpen,
                        setSnackbarMessage,
                        setSnackbarSeverity,
                      });
                    },
                  },
                  isGoogleCalendarConnectionCandidate(user)
                    ? {
                        icon: (props) => (
                          <Icon
                            baseClassName="material-symbols-outlined"
                            {...props}
                          >
                            sync
                          </Icon>
                        ),
                        name: "Switch to Google Connect",
                        action: async (calendar) => {
                          const performAction = () => {
                            const upgradeFlow = getUpgradeFlow(
                              Features.CONNECT_GOOGLE_CALENDAR.key,
                              {
                                handle: calendar?.handle,
                                calendarId: calendar?._id,
                              }
                            );
                            navigate(upgradeFlow.route, {
                              state: { ...upgradeFlow.state, calendar },
                            });
                          };

                          await requestFeatureAccess(
                            Features.CONNECT_GOOGLE_CALENDAR.key,
                            {
                              calendar,
                              handle: calendar?.handle,
                              calendarId: calendar?._id,
                              currentPath: window.location.pathname,
                              action: performAction,
                              continueText: "Switch to Google Connect",
                            }
                          );
                          // Context handles navigation after dialog
                        },
                      }
                    : null,
                  {
                    icon: (props) => (
                      <Icon
                        baseClassName="material-symbols-outlined"
                        {...props}
                      >
                        share
                      </Icon>
                    ),
                    name: "Share",
                    action: (calendar) => {
                      setShareDialogCalendar(calendar);
                    },
                  },
                  {
                    icon: (props) => (
                      <Icon
                        baseClassName="material-symbols-outlined"
                        {...props}
                      >
                        delete
                      </Icon>
                    ),
                    name: "Delete",
                    color: "error",
                    action: (calendar) =>
                      handleUnsubscribe({
                        user,
                        calendar,
                        unsubscribeGroup,
                        refetchUserCalendars,
                        createdGroups: userCalendars.createdGroups,
                        removeFromGroup,
                        unsubscribeCalendar,
                        onUnsubscribed: async () => {
                          setAddedItems(
                            addedItems.filter((c) => c._id !== calendar._id)
                          );
                          refetchAuth();
                        },
                        showSnackBar,
                        setSnackbarOpen,
                        setSnackbarMessage,
                        setSnackbarSeverity,
                      }),
                  },
                ].filter(Boolean)}
              />
              {/* {isGoogleCalendarConnectionCandidate(user) &&
                (
                  <GoogleConnectUpgradeBanner
                    sx={{ mt: { xs: -2, sm: -8 } }}
                  />
                )} */}
              {!hasAccess(user, Features.ADD_CALENDAR.key) && (
                <UnlimitedCalendarsUpgradeBanner
                  sx={{ mt: { xs: -2, sm: -8 } }}
                />
              )}
            </>
          ) : (
            <ActionCard
              title="No calendars"
              testid="user-no-added-calendar"
              description="You haven't added any calendars yet."
              primaryButtonCTA="Discover calendars"
              primaryAction={() => navigate(getPageRoute("home", "HOME"))}
              iconName="calendar_month"
              sx={
                user.consumerSubscription?.isCancelled
                  ? {
                      top: { xs: "43vh", md: "35vh" },
                    }
                  : {}
              }
            />
          )}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== "groups"}>
          <LibraryMessage user={user} tab="group" sx={{ mb: 2 }} />
          {!hasAccess(user, Features.CREATE_GROUP.key) && (
            <GroupsUpgradeBanner sx={{ mt: 4 }} />
          )}
          {isLoading || initialLoad ? (
            <LoadingAnimation />
          ) : createdGroups.length > 0 ? (
            <CalendarList
              id={"groups"}
              user={user}
              calendars={createdGroups}
              {...(!hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)
                ? {
                    buttonIcon: (props) => (
                      <Icon
                        baseClassName="material-symbols-outlined"
                        {...props}
                      >
                        event_repeat
                      </Icon>
                    ),
                    buttonText: "Resubscribe",
                    buttonAction: handleResubscribe,
                  }
                : {})}
              googleCalendarStatus={googleCalendarStatus}
              isLoadingForceRefreshGoogleCalendar={
                isLoadingForceRefreshGoogleCalendar
              }
              forceRefreshCalendarId={forceRefreshCalendarId}
              threeDotsMenu={[
                {
                  icon: (props) => <EditOutlined {...props} />,
                  name: "Edit",
                  action: (calendar) =>
                    navigate(
                      getPageRoute("calendar", "CALENDAR_SETTINGS", {
                        handle: user.handle,
                        calendarId: calendar._id,
                      }),
                      {
                        state: { calendar: calendar },
                      }
                    ),
                },
                hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)
                  ? {
                      icon: (props) => (
                        <Icon
                          baseClassName="material-symbols-outlined"
                          {...props}
                        >
                          refresh
                        </Icon>
                      ),
                      name: "Force Refresh",
                      action: handleForceRefreshGoogleCalendar,
                      isDisabled: isDisabledForceRefresh,
                    }
                  : null,
                {
                  icon: (props) => (
                    <Icon baseClassName="material-symbols-outlined" {...props}>
                      link
                    </Icon>
                  ),
                  name: "Copy Link",
                  action: (calendar) => {
                    copyCalendarLinkToClipboard({
                      inGroup: false,
                      user,
                      calendar,
                      showSnackBar,
                      setSnackbarOpen,
                      setSnackbarMessage,
                      setSnackbarSeverity,
                    });
                  },
                },
                isGoogleCalendarConnectionCandidate(user)
                  ? {
                      icon: (props) => (
                        <Icon
                          baseClassName="material-symbols-outlined"
                          {...props}
                        >
                          sync
                        </Icon>
                      ),
                      name: "Switch to Google Connect",
                      action: async (calendar) => {
                        const performAction = () => {
                          const upgradeFlow = getUpgradeFlow(
                            Features.CONNECT_GOOGLE_CALENDAR.key,
                            {
                              handle: calendar?.handle,
                              calendarId: calendar?._id,
                            }
                          );
                          navigate(upgradeFlow.route, {
                            state: { ...upgradeFlow.state, calendar },
                          });
                        };

                        await requestFeatureAccess(
                          Features.CONNECT_GOOGLE_CALENDAR.key,
                          {
                            calendar,
                            handle: calendar?.handle,
                            calendarId: calendar?._id,
                            currentPath: window.location.pathname,
                            action: performAction,
                            continueText: "Switch to Google Connect",
                          }
                        );
                        // Context handles navigation after dialog
                      },
                    }
                  : null,
                {
                  icon: (props) => (
                    <Icon baseClassName="material-symbols-outlined" {...props}>
                      share
                    </Icon>
                  ),
                  name: "Share",
                  action: (calendar) => {
                    setShareDialogCalendar(calendar);
                  },
                },
                {
                  icon: (props) => (
                    <Icon baseClassName="material-symbols-outlined" {...props}>
                      delete
                    </Icon>
                  ),
                  name: "Delete",
                  color: "error",
                  action: handleClickOpenDeleteDialog,
                },
              ].filter(Boolean)}
            />
          ) : (
            user?.hasValidSubscription && (
              <ActionCard
                title="No groups"
                testid="user-no-created-group"
                description="You haven't created any groups yet."
                primaryButtonCTA="Discover calendars"
                primaryAction={() => navigate(getPageRoute("home", "HOME"))}
                iconName="folder"
                sx={
                  user.consumerSubscription?.isCancelled
                    ? {
                        top: { xs: "43vh", md: "35vh" },
                      }
                    : {}
                }
              />
            )
          )}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== "imports"}>
          {user.createdCalendars.length > user.maxNumCreatedCalendars ? (
            <Paper
              variant="outlined"
              sx={{ p: 2, mt: 2 }}
              data-testid="library-canceled-creator-copy"
            >
              <Typography variant="subtitle2" sx={{ lineHeight: 1.5 }}>
                ⚠️ Your current plan allows you to import{" "}
                {user.maxNumCreatedCalendars} calendar
                {user.maxNumCreatedCalendars === 1 ? "" : "s"}. Please delete{" "}
                {importedCalendars.length - user.maxNumCreatedCalendars}{" "}
                calendar
                {importedCalendars.length - user.maxNumCreatedCalendars === 1
                  ? ""
                  : "s"}{" "}
                by{" "}
                {user.consumerSubscription
                  ? formatDate(user.creatorSubscription?.expiresAt)
                  : ""}
                .{" "}
                <strong>
                  Any extra calendars will be automatically deleted.
                </strong>{" "}
                <br />
                <br />
                If you change your mind, you can{" "}
                <Link
                  onClick={handleCustomerPortal}
                  style={{ textDecoration: "none" }}
                >
                  update your subscription
                </Link>{" "}
                to keep all your calendars.
              </Typography>
            </Paper>
          ) : null}
          {isLoading || initialLoad ? (
            <LoadingAnimation />
          ) : importedCalendars.length === 0 ? (
            <ActionCard
              title="No imports"
              testid="user-no-imported-calendar"
              description="You haven't imported any calendars yet."
              primaryButtonCTA="Import calendar"
              primaryAction={() => navigate(getPageRoute("calendar", "IMPORT"))}
              iconName="calendar_apps_script"
            />
          ) : (
            <CalendarList
              id={"imports"}
              user={user}
              calendars={importedCalendars}
              buttonIcon={(props) => <EditOutlined {...props} />}
              buttonText="Edit"
              buttonAction={(calendar) =>
                navigate(
                  getPageRoute("calendar", "CALENDAR_SETTINGS", {
                    handle: user.handle,
                    calendarId: calendar._id,
                  }),
                  {
                    state: { calendar: calendar },
                  }
                )
              }
              threeDotsMenu={[
                {
                  icon: (props) => (
                    <Icon baseClassName="material-symbols-outlined" {...props}>
                      link
                    </Icon>
                  ),
                  name: "Copy Link",
                  action: (calendar) => {
                    copyCalendarLinkToClipboard({
                      inGroup: false,
                      user,
                      calendar,
                      showSnackBar,
                      setSnackbarOpen,
                      setSnackbarMessage,
                      setSnackbarSeverity,
                    });
                  },
                },
                {
                  icon: (props) => (
                    <Icon baseClassName="material-symbols-outlined" {...props}>
                      delete
                    </Icon>
                  ),
                  name: "Delete",
                  color: "error",
                  action: handleClickOpenDeleteDialog,
                },
              ]}
            />
          )}
        </Box>
      </Box>
      <ShareDialog
        calendar={shareDialogCalendar}
        handle={shareDialogCalendar?.handle}
        open={Boolean(shareDialogCalendar)}
        setOpen={() => setShareDialogCalendar(null)}
        as="Hidden"
      />

      <NewGroupDialog
        open={openNewGroup}
        handleClose={() => setOpenNewGroup(false)}
        handleCreateGroup={handleCreateGroup}
        user={user}
        calendarId={targetCalendar._id}
        handleMessage={handleMessage}
      />
      <OpenCalendarDialog
        open={openCalendarDialog}
        handleClose={() => setOpenCalendarDialog(false)}
        calendar={selectedCalendar.calendar}
      />
      <AddToGroupDialog
        createdGroups={createdGroups}
        open={openAddToGroup}
        handleClose={() => setOpenAddToGroup(false)}
        handleCreate={() => {
          setOpenAddToGroup(false);
          setOpenNewGroup(true);
        }}
        handleAdd={handleAddToGroup}
      />
      <DeleteCalendarDialog
        open={openCalendar}
        handleClose={handleCloseDeleteDialog}
        handleDelete={handleDelete}
        isLoading={isLoadingDelete}
        deleteText={deleteText}
        deleteMessage={deleteMessage}
        setDeleteText={setDeleteText}
      />
      <DeleteGroupDialog
        open={openGroup}
        handleClose={handleCloseDeleteDialog}
        handleDelete={handleDelete}
        isLoading={isLoadingDelete}
        deleteText={deleteText}
        deleteMessage={deleteMessage}
        setDeleteText={setDeleteText}
      />
      <Footer />
    </div>
  );
}

export default Library;
